import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AssignedProduct, Campaign } from '@discovr/types';
import { campaignKeys } from '../queryKeys';
import { errorMessage } from '../../copy';
import { createRosterMutationsApi } from '../api/rosterMutations';
import {
  applyOptimisticProductAssignment,
  rollbackRosterPages,
  type CreatorsCacheSnapshot,
} from '../rosterOptimisticCache';

type AssignVars = { entryId: string; productIds: string[] };
type AssignCtx = { snapshot: CreatorsCacheSnapshot };

/**
 * useAssignProducts — full-replace a confirmed creator's assigned products via
 * PATCH /roster/:id/products (Multi-Product Campaigns).
 *
 * Optimistic + reconcile: the row's chips update instantly by patching every
 * cached roster page in place (both the paged {entries} shape and the bare-array
 * shape the Confirmed/Live pills use). Then onSettled forces an ACTIVE refetch so
 * pills whose membership depends on the assignment reconcile from server truth —
 * the Confirmed tab's Unassigned pill filters `assigned_products.length === 0` at
 * fetch time, so an in-place patch alone can't move a creator in/out of it. The
 * refetch is flash-free: useRosterStatusTabs keeps `isLoading` false while data
 * stays defined (placeholderData: keepPreviousData), so the table reconciles in
 * the background without a skeleton. Rolls back on error. Pass the complete
 * desired id set every call (empty = unassign all).
 */
export function useAssignProducts(campaignId: string) {
  const queryClient = useQueryClient();
  const api = useMemo(() => createRosterMutationsApi(), []);

  return useMutation<void, Error, AssignVars, AssignCtx>({
    mutationFn: ({ entryId, productIds }) => api.assignProducts(entryId, productIds),

    onMutate: async ({ entryId, productIds }) => {
      // Resolve ids → resolved products from the campaign catalog in cache, so
      // the optimistic chips carry the same name the server will return.
      const campaign = queryClient.getQueryData<Campaign>(campaignKeys.detail(campaignId));
      const byId = new Map((campaign?.products ?? []).map((p) => [p.product_id, p]));
      const resolved: AssignedProduct[] = productIds.flatMap((id) => {
        const p = byId.get(id);
        return p ? [{ product_id: p.product_id, name: p.name, product_url: p.product_url, notes: p.notes }] : [];
      });

      // Halt in-flight roster refetches so they can't clobber the optimistic write.
      await queryClient.cancelQueries({ queryKey: campaignKeys.rosterAll(campaignId) });
      const snapshot = applyOptimisticProductAssignment(queryClient, campaignId, entryId, resolved);
      return { snapshot };
    },

    onError: (err, _vars, ctx) => {
      if (ctx) rollbackRosterPages(queryClient, ctx.snapshot);
      toast.error(errorMessage(err, 'Failed to assign products'));
    },

    onSettled: () => {
      // Active refetch: reconcile from server truth so membership-filtered pills
      // (the Unassigned pill re-filters assigned_products.length === 0) are
      // correct. Flash-free — keepPreviousData holds the rows during the refetch,
      // so isLoading stays false and the table never flips to its skeleton.
      queryClient.invalidateQueries({
        queryKey: campaignKeys.rosterAll(campaignId),
        refetchType: 'active',
      });
    },
  });
}
